import type { ReactNode } from 'react';
import { cn } from '@/components/ui';

/*
  SideZone — место второго и третьего уровня меню справа от главного (docs/foundation.md §3–4).

  Зона заходит под главное меню на 16 px и перекрывает зазор рамки: второй уровень выезжает
  из-под главного меню и прилегает к нему вплотную, а за скруглёнными углами меню виден его фон.
  Третий уровень стоит в той же зоне отдельной карточкой с обычным зазором. Ширина открытой
  зоны одна для обоих уровней, поэтому при смене второго уровня на третий край страницы стоит
  на месте; двигается он только при уходе с главной в раздел.

  Оба уровня отрисованы всегда: уходящий доигрывает свой выход, пока приходит другой. Показанный уровень —
  карточка в плане дома помощника (data-shell-card="zone", spec-3-motion §1); пока зона выезжает
  (data-shell-zone), помощник ждёт — раскладка не в покое.
*/

export type SideMode = 'none' | 'section' | 'app';

interface SideZoneProps {
  mode: SideMode;
  /** Меню раздела (уровень 2) — последнее показанное, чтобы оно уехало со своим содержимым. */
  section: ReactNode;
  /** Меню приложения (уровень 3). */
  app: ReactNode;
}

export function SideZone({ mode, section, app }: SideZoneProps) {
  const sectionShown = mode === 'section';
  const appShown = mode === 'app';
  return (
    <div
      data-shell-zone=""
      className={cn('relative side-zone shrink-0 overflow-hidden', mode === 'none' ? 'w-4' : 'w-side')}
    >
      <div
        inert={!sectionShown}
        aria-hidden={!sectionShown}
        data-shell-card={sectionShown ? 'zone' : undefined}
        className={cn(
          'absolute inset-y-0 left-0 w-side transition-transform duration-slow ease-in-out',
          sectionShown ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {section}
      </div>
      <div
        inert={!appShown}
        aria-hidden={!appShown}
        data-shell-card={appShown ? 'zone' : undefined}
        className={cn(
          'absolute inset-y-0 left-7 w-64 transition duration-smooth ease-out',
          appShown
            ? 'translate-x-0 scale-100 opacity-100 delay-200'
            : 'pointer-events-none -translate-x-4 scale-95 opacity-0',
        )}
      >
        {app}
      </div>
    </div>
  );
}
